/**
 * Tous les textes affichés par le module. Une langue se choisit par `langue`, et
 * n'importe quel texte se remplace par `textes` dans la configuration.
 * Les `{nom}` sont remplacés par les valeurs du moment.
 */
export interface Textes {
  titre: string
  choisir: string
  changer: string
  voir: string
  retirer: string
  aucune: string
  aucuneLecture: string
  annuler: string
  fermer: string
  valider: string
  selection: string
  requis: string
  libelleChamp: string
  rechercheSite: string
  rechercheAria: string
  coordonnees: string
  coordonneesAide: string
  coordonneesAria: string
  localiser: string
  effacer: string
  zoomer: string
  maille: string
  region: string
  enMer: string
  site: string
  sites: string
  aucunSite: string
  centroide: string
  pointSaisi: string
  lu: string
  inconnue: string
  inconnueDetail: string
  chargement: string
  donneesIntrouvables: string
  indisponible: string
  accesRefuse: string
  horsGrille: string
  groupeAria: string
  // Lecture des coordonnées
  err_vide: string
  err_nombres: string
  err_coordonnees: string
  err_minutes: string
  err_secondes: string
  err_hors_emprise: string
  err_hemisphere_lat: string
  err_hemisphere_lon: string
  rq_dms: string
  rq_ordre: string
  rq_signe_lat: string
  rq_signe_lon: string
}

export const FR: Textes = {
  titre: 'Choisir une maille',
  choisir: 'Choisir sur la carte',
  changer: 'Changer',
  voir: 'Voir sur la carte',
  retirer: 'Retirer la maille',
  aucune: 'Aucune maille sélectionnée',
  aucuneLecture: 'Aucune maille',
  annuler: 'Annuler',
  fermer: 'Fermer',
  valider: 'Valider cette maille',
  selection: 'Sélection : {resume}',
  requis: 'Choisissez une maille sur la carte.',
  libelleChamp: 'Maille de la grille',
  rechercheSite: 'Aller à un site (nom ou code)…',
  rechercheAria: 'Rechercher un site',
  coordonnees: 'Coordonnées : {exemple}',
  coordonneesAide: 'Latitude, longitude en degrés décimaux ({exemple}) ou en degrés-minutes-secondes (18°54\'49"S 47°32\'10"E)',
  coordonneesAria: 'Coordonnées géographiques (latitude, longitude)',
  localiser: 'Localiser',
  effacer: 'Effacer',
  zoomer: 'Zoomez, ou cliquez sur la carte, pour afficher et sélectionner les mailles.',
  maille: 'Maille {code}',
  region: 'Région : {region}',
  enMer: '— (hors région)',
  site: 'Site : {sites}',
  sites: 'Sites : {sites}',
  aucunSite: 'Site : aucun',
  centroide: 'Centroïde : {coord}',
  pointSaisi: 'Point saisi : {coord}',
  lu: 'Lu : {coord} — {remarques}.',
  inconnue: 'Maille {id} inconnue',
  inconnueDetail: 'L’identifiant {id} ne correspond à aucune maille de la grille.',
  chargement: 'Maille {id}…',
  donneesIntrouvables: 'Données de la grille introuvables ({detail}).',
  indisponible: 'Carte indisponible',
  accesRefuse: 'Accès aux données de la carte refusé : clé absente ou invalide.',
  horsGrille: 'Aucune maille à ce point ({coord}) : il est hors de la grille.',
  groupeAria: 'Carte de sélection de maille',
  err_vide: 'Saisissez une latitude et une longitude.',
  err_nombres: 'Deux nombres attendus (latitude et longitude), {n} trouvé(s).',
  err_coordonnees: 'Deux coordonnées attendues (latitude et longitude), {n} reconnue(s).',
  err_minutes: 'Minutes invalides ({valeur}) : elles doivent être entre 0 et 59.',
  err_secondes: 'Secondes invalides ({valeur}) : elles doivent être entre 0 et 59.',
  err_hors_emprise: 'Coordonnées hors de la zone couverte par la grille (lu : {a}, {b}).',
  err_hemisphere_lat: 'Hémisphère incohérent : la grille est entièrement au {attendu}.',
  err_hemisphere_lon: 'Hémisphère incohérent : la grille est entièrement à l’{attendu}.',
  rq_dms: 'converti depuis les degrés-minutes-secondes',
  rq_ordre: 'longitude saisie en premier : ordre rétabli',
  rq_signe_lat: 'signe de la latitude déduit de la zone couverte',
  rq_signe_lon: 'signe de la longitude déduit de la zone couverte',
}

export const EN: Textes = {
  titre: 'Choose a grid cell',
  choisir: 'Choose on the map',
  changer: 'Change',
  voir: 'View on the map',
  retirer: 'Remove the cell',
  aucune: 'No cell selected',
  aucuneLecture: 'No cell',
  annuler: 'Cancel',
  fermer: 'Close',
  valider: 'Use this cell',
  selection: 'Selected: {resume}',
  requis: 'Please choose a cell on the map.',
  libelleChamp: 'Grid cell',
  rechercheSite: 'Go to a site (name or code)…',
  rechercheAria: 'Search a site',
  coordonnees: 'Coordinates: {exemple}',
  coordonneesAide: 'Latitude, longitude in decimal degrees ({exemple}) or degrees-minutes-seconds (18°54\'49"S 47°32\'10"E)',
  coordonneesAria: 'Geographic coordinates (latitude, longitude)',
  localiser: 'Locate',
  effacer: 'Clear',
  zoomer: 'Zoom in, or click the map, to show and select cells.',
  maille: 'Cell {code}',
  region: 'Region: {region}',
  enMer: '— (outside any region)',
  site: 'Site: {sites}',
  sites: 'Sites: {sites}',
  aucunSite: 'Site: none',
  centroide: 'Centroid: {coord}',
  pointSaisi: 'Entered point: {coord}',
  lu: 'Read: {coord} — {remarques}.',
  inconnue: 'Unknown cell {id}',
  inconnueDetail: 'Identifier {id} does not match any cell of the grid.',
  chargement: 'Cell {id}…',
  donneesIntrouvables: 'Grid data not found ({detail}).',
  indisponible: 'Map unavailable',
  accesRefuse: 'Access to the map data denied: missing or invalid key.',
  horsGrille: 'No cell at this point ({coord}): it is outside the grid.',
  groupeAria: 'Grid cell selection map',
  err_vide: 'Enter a latitude and a longitude.',
  err_nombres: 'Two numbers expected (latitude and longitude), {n} found.',
  err_coordonnees: 'Two coordinates expected (latitude and longitude), {n} recognised.',
  err_minutes: 'Invalid minutes ({valeur}): they must be between 0 and 59.',
  err_secondes: 'Invalid seconds ({valeur}): they must be between 0 and 59.',
  err_hors_emprise: 'Coordinates outside the area covered by the grid (read: {a}, {b}).',
  err_hemisphere_lat: 'Inconsistent hemisphere: the grid lies entirely in the {attendu}.',
  err_hemisphere_lon: 'Inconsistent hemisphere: the grid lies entirely in the {attendu}.',
  rq_dms: 'converted from degrees-minutes-seconds',
  rq_ordre: 'longitude entered first: order restored',
  rq_signe_lat: 'latitude sign inferred from the covered area',
  rq_signe_lon: 'longitude sign inferred from the covered area',
}

const POINTS: Record<string, Record<string, string>> = {
  fr: { N: 'Nord', S: 'Sud', E: 'Est', O: 'Ouest' },
  en: { N: 'North', S: 'South', E: 'East', O: 'West' },
}

export function texteur(langue: 'fr' | 'en', surcharges: Partial<Textes> = {}) {
  const t: Textes = { ...(langue === 'en' ? EN : FR), ...surcharges }
  return (cle: keyof Textes, valeurs: Record<string, string | number> = {}): string =>
    t[cle].replace(/\{(\w+)\}/g, (_, k: string) => {
      const v = valeurs[k]
      if (v === undefined) return `{${k}}`
      return k === 'attendu' ? (POINTS[langue][String(v)] ?? String(v)) : String(v)
    })
}

export type Texteur = ReturnType<typeof texteur>
